import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Unlink, Workflow } from 'lucide-react';
import type { ImpactedProcess } from '@bliks-io/contracts';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  getColorSchemeFromString,
  Button,
  ScoreBadge,
  DataTable,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { CapacityCell } from '@/components/capacity-cell';
import { StepExplorerModal } from './StepExplorerModal';

interface ProcessesImpactedCardProps {
  processes: ImpactedProcess[];
  canViewSensitiveFields?: boolean;
}

interface ModalState {
  open: boolean;
  process: ImpactedProcess | null;
}

export function ProcessesImpactedCard({
  processes,
  canViewSensitiveFields
}: ProcessesImpactedCardProps) {
  const { t } = useTranslation();
  // Use single state object to avoid race conditions between modal open state and selected process
  const [modalState, setModalState] = useState<ModalState>({ open: false, process: null });

  const handleViewSteps = useCallback((process: ImpactedProcess) => {
    setModalState({ open: true, process });
  }, []);

  const handleModalClose = useCallback((open: boolean) => {
    setModalState(open ? (prev) => prev : { open: false, process: null });
  }, []);

  const columns: DataTableColumnDef<ImpactedProcess>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: createColumnHeader(t('organization.systems.processesImpacted.processName')),
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.name}</div>
            {row.original.department && (
              <Badge
                shape="rounded"
                colorScheme={getColorSchemeFromString(row.original.department._id)}
                className="mt-1"
              >
                {row.original.department.abbreviation || row.original.department.name}
              </Badge>
            )}
          </div>
        )
      },
      {
        accessorKey: 'stepsInvolved',
        header: createColumnHeader(t('organization.systems.processesImpacted.stepsInvolved')),
        cell: ({ row }) => (
          <Badge shape="circle" colorScheme="gray">
            {row.original.stepsInvolved}
          </Badge>
        ),
        meta: { fitContent: true }
      },
      ...(canViewSensitiveFields
        ? [
            {
              accessorKey: 'averageRating',
              header: createColumnHeader(t('organization.systems.processesImpacted.avgRating')),
              cell: ({ row }) => (
                <ScoreBadge
                  value={row.original.averageRating}
                  variant="boxed"
                  colorScheme="dynamic"
                />
              ),
              meta: { fitContent: true }
            } satisfies DataTableColumnDef<ImpactedProcess>
          ]
        : []),
      {
        accessorKey: 'mediaBreakCount',
        header: createColumnHeader(t('organization.systems.processesImpacted.mediaBreaks')),
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Unlink className="h-4 w-4 text-danger" />
            <span>{row.original.mediaBreakCount}</span>
          </div>
        ),
        meta: { fitContent: true }
      },
      ...(canViewSensitiveFields
        ? [
            {
              accessorKey: 'capacityBinding',
              header: createColumnHeader(t('organization.systems.processesImpacted.capacity')),
              cell: ({ row }: { row: { original: ImpactedProcess } }) => (
                <CapacityCell capacity={row.original.capacityBinding} />
              ),
              meta: { fitContent: true },
              sortFn: (rowA, rowB) => {
                const a = rowA.original.capacityBinding?.hoursPerMonth ?? 0;
                const b = rowB.original.capacityBinding?.hoursPerMonth ?? 0;
                return a - b;
              }
            } satisfies DataTableColumnDef<ImpactedProcess>
          ]
        : []),
      {
        id: 'actions',
        header: () => (
          <span className="sr-only">{t('organization.systems.processesImpacted.action')}</span>
        ),
        cell: ({ row }) => (
          <Button variant="link" size="small" onClick={() => handleViewSteps(row.original)}>
            {t('organization.systems.processesImpacted.viewSteps')}
          </Button>
        ),
        meta: { fitContent: true }
      }
    ],
    [t, handleViewSteps, canViewSensitiveFields]
  );

  return (
    <>
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5" />
            {t('organization.systems.processesImpacted.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={processes}
            emptyMessage={t('organization.systems.processesImpacted.noProcesses')}
            enablePagination
            initialPageSize={5}
            pageSizeOptions={[5, 10, 20]}
          />
        </CardContent>
      </Card>

      <StepExplorerModal
        process={modalState.process}
        open={modalState.open}
        onOpenChange={handleModalClose}
        canViewSensitiveFields={canViewSensitiveFields}
      />
    </>
  );
}
